import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import idb from "./store/idb";
import moment from "moment";
import {API_DOMAIN} from "./utils/constants";
import {assign, parseInt} from 'lodash';
import packageInfo from '../package.json';
import { Capacitor } from '@capacitor/core';
import { setBugsnagUser, clearBugsnagUser } from './utils/bugsnag';

axios.interceptors.request.use(function (config) {
  config.headers['X-App-Version'] = packageInfo.version;
  config.headers['X-Platform'] = Capacitor.getPlatform();
  return config;
});

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    appVersion: packageInfo.version,
    status: '',
    token: localStorage.getItem('token') || '',
    unauthTrialEndsAt: localStorage.getItem('unauthTrialEndsAt') || '',
    user: {},
    apiURL: API_DOMAIN + '/api/',
    assetPath: '',
    project: {
      id: null,
      name: null,
      published_at: null,
      updated_at: null,
      created_at: null,
      congrid_project_id: null,
      user: {
        role: null
      }
    },
    company: {
      id: null
    },
    companies: [],
    isLoading: false,
    loadingText: 'Loading...',
    isOnline: true,
    hasChanges: false
  },
  mutations: {
    auth_request(state) {
      state.status = 'loading';
    },
    auth_success(state, data) {
      state.status = 'success';
      state.token = data.token;
      state.user = data.user;

      // Set Bugsnag user context (only on native platforms)
      if (
        Capacitor.getPlatform() !== 'web' &&
        data.user &&
        (typeof data.user.id !== 'undefined' ||
          typeof data.user.email !== 'undefined' ||
          typeof data.user.name !== 'undefined')
      ) {
        setBugsnagUser(data.user);
      }
    },
    auth_error(state) {
      state.status = 'error';
    },
    logout(state) {

      state.status = '';
      state.token = '';
      state.user = {};
      state.project = {
        id: null,
        name: null,
        published_at: null,
        updated_at: null,
        created_at: null,
        user: {
          role: null
        }
      };

      state.company = {
        id: null
      };
      state.unauthTrialEndsAt = '';
      state.companies = [];

      // Clear Bugsnag user context (only on native platforms)
      if (Capacitor.getPlatform() !== 'web') {
        clearBugsnagUser();
      }

      idb.clearDB().then(() => {
      });
    },
    project(state, project) {
      if (project === null) {
        state.project.id = null;
        state.project.name = null;
        state.project.published_at = null;
        state.project.updated_at = null;
        state.project.created_at = null;
      } else {
        assign(state.project, project);
      }
    }
  },
  actions: {
    register({commit}, user) {
      this.state.isLoading = true;
      this.state.loadingText = 'Registering...';

      return new Promise((resolve, reject) => {
        axios({url: this.state.apiURL + 'auth/register', data: user, method: 'POST'})
          .then(resp => {
            this.state.isLoading = false;
            resolve(resp);
          })
          .catch(err => {
            this.state.isLoading = false;
            reject(err);
          });
      });
    },
    login({commit}, user) {
      this.state.isLoading = true;
      this.state.loadingText = 'Logging in...';

      let that = this;

      return new Promise((resolve, reject) => {
        commit('auth_request');

        axios({url: this.state.apiURL + 'auth/login', data: user, method: 'POST'})
          .then(resp => {
            const token = resp.data.token;
            axios.defaults.headers.common['Authorization'] = token;
            localStorage.setItem('token', token);

            axios({
              url: this.state.apiURL + 'auth/me',
              method: 'POST',
              headers: {"Authorization": "Bearer " + token}
            })
              .then(response => {
                const user = response.data;
                idb.saveUser(user).then(() => {
                  commit('auth_success', {token, user});
                  that.state.isLoading = false;
                  resolve(resp);
                });
              });
          })
          .catch(err => {
            this.state.isLoading = false;
            commit('auth_error');
            localStorage.removeItem('token');
            reject(err);
          });
      });
    },

    loadCachedUser({commit}) {
      return idb.getUser().then(user => {
        var token = this.state.token;

        if (!token || !user) {
          return Promise.reject(new Error('No active user'));
        }
        commit('auth_success', {token, user});

        return user;
      });
    },

    logout({commit}) {
      this.state.isLoading = true;
      this.state.loadingText = 'Logging out...';

      return new Promise((resolve, reject) => {
        commit('logout');
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        idb.clearDB().then(() => {
          this.state.isLoading = false;
          resolve();
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },
    forgotPassword({commit}, email) {
      this.state.isLoading = true;
      this.state.loadingText = 'Please wait...';

      return new Promise((resolve, reject) => {

        axios({url: this.state.apiURL + 'auth/reset-password', data: {email: email}, method: 'POST'})
          .then(resp => {
            resolve(resp.data);
            this.state.isLoading = false;
          })
          .catch(err => {
            this.state.isLoading = false;
            reject(err);
          });
      });
    },
    startUnauthTrial({commit}) {
      var unauthTrialEndsAt = localStorage.getItem('unauthTrialEndsAt');

      if (unauthTrialEndsAt) {
        return false;
      }
      var in4Days = moment().add(4, 'days').toISOString();

      localStorage.setItem('unauthTrialEndsAt', in4Days);

      this.state.unauthTrialEndsAt = in4Days;

      return true;
    },
    async get_folders_by_parent_id(parentID = null) {


      this.state.isLoading = true;
      this.state.loadingText = 'Getting folders...';

      return new Promise((resolve, reject) => {
        idb.getFoldersByParentID(parentID).then(folders => {
          this.state.isLoading = false;
          resolve(folders);
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });

    },
    async get_folders() {


      this.state.isLoading = true;
      this.state.loadingText = 'Getting folders...';

      return new Promise((resolve, reject) => {
        // commit('get_files');

        idb.getFolders().then(folders => {
          this.state.isLoading = false;
          resolve(folders);
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });

    },
    async store_file({commit}, file) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Saving file...';

      return new Promise((resolve, reject) => {

        idb.saveFile(file).then(fileResult => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }

          resolve(fileResult);
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },
    async update_file({commit}, file) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Updating file...';

      return new Promise((resolve, reject) => {
        idb.saveFile(file).then(() => {
          this.state.isLoading = false;

          resolve('File updated');
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },
    async get_files({commit}, fileTypes) {
      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }


      this.state.loadingText = 'Getting files...';

      return new Promise((resolve, reject) => {
        // commit('get_files');

        idb.getFiles(fileTypes).then(files => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }
          resolve(files);
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });

    },
    async get_files_by_folder(folderID) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }


      this.state.loadingText = 'Getting files...';

      return new Promise((resolve, reject) => {
        idb.getFilesByFolderID(folderID).then(files => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }
          resolve(files);
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });

    },

    async get_file({commit}, fileID) {
      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Getting file...';

      return new Promise((resolve, reject) => {
        idb.getFile(fileID).then(file => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }
          resolve(file);
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },


    async get_file_by_file_id({commit}, fileID) {
      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Getting file...';

      return new Promise((resolve, reject) => {
        idb.getFileByFileID(fileID).then(file => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }
          resolve(file);
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });

    },
    async get_files_by_project({commit}, projectID) {
      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }


      this.state.loadingText = 'Getting files...';

      return new Promise((resolve, reject) => {
        // commit('get_files');

        idb.getProjectFiles(projectID).then(files => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }
          resolve(files);
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });

    },
    async delete_file({commit}, fileID) {
      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Deleting file...';

      return new Promise((resolve, reject) => {
        idb.deletePdfPages(fileID).then(() => {
          idb.deleteFileMarkings(fileID);
        }).then(() => {
          idb.deleteFile(fileID);
        }).then(() => {
          idb.deleteFileCollaborateMarkings(fileID);
        }).then(() => {
          idb.deleteTeammatesCache(fileID);
        }).then(() => {
          resolve();
        }).catch(err => {
          console.log('Failed to delete', err);
          this.state.isLoading = false;
          reject(err);
        });
      });

    },
    async delete_files({commit}, fileIds) {
      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Deleting files...';

      return new Promise(async (resolve, reject) => {
        try {
          for (const fileId of fileIds) {
            await idb.deletePdfPages(fileId);
            await idb.deleteFileMarkings(fileId);
            await idb.deleteFile(fileId);
            await idb.deleteFileCollaborateMarkings(fileId);
            await idb.deleteTeammatesCache(fileId);
          }
          resolve();
        } catch (err) {
          console.log('Failed to delete files.', err);
          this.state.isLoading = false;
          reject(err);
        }
      });
    },
    async store_marking({commit}, marking) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Storing marking...';

      return new Promise((resolve, reject) => {
        idb.saveMarking(marking).then(() => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }
          resolve();
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },
    async get_markings({commit}, fileID) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Getting markings...';

      return new Promise((resolve, reject) => {
        idb.getMarkings(fileID).then(markings => {
          this.state.isLoading = false;

          resolve(markings);


        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },
    async deleteMarking({commit}, markingID) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }
      this.state.loadingText = 'Optimizing markings...';

      return new Promise((resolve, reject) => {
        idb.deleteMarking(markingID).then(() => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }

          resolve();
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },

    async store_collaborate_marking({commit}, marking) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Storing collaborate marking...';

      return new Promise((resolve, reject) => {
        idb.saveCollaborateMarking(marking).then(() => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }
          resolve();
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },
    async get_collaborate_markings({commit}, fileID) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Getting collaborate markings...';

      return new Promise((resolve, reject) => {
        idb.getCollaborateMarkings(fileID).then(markings => {
          this.state.isLoading = false;

          resolve(markings);

        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },
    async delete_collaborate_marking({commit}, markingID) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }
      this.state.loadingText = 'Optimizing collaborate markings...';

      return new Promise((resolve, reject) => {
        idb.deleteCollaborateMarking(markingID).then(() => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }

          resolve();
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },
    async delete_file_collaborate_markings({commit}, fileID) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Optimizing collaborate markings...';

      return new Promise((resolve, reject) => {
        idb.deleteFileCollaborateMarkings(fileID).then(() => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }

          resolve();
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },
    async get_collaborate_teammates_cache({commit}, fileID) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      return new Promise((resolve, reject) => {
        idb.getTeammatesCache(fileID).then(cache => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }

          resolve(cache);
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },
    async store_collaborate_teammates_cache({commit}, {fileID, teammates}) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      return new Promise((resolve, reject) => {
        idb.saveTeammatesCache(fileID, teammates).then(() => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }

          resolve();
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },
    async syncCollaborateMarkings({commit}, data) {

      this.state.isLoading = true;
      this.state.loadingText = 'Synchronizing collaborate markings...';
      await new Promise(resolve => setTimeout(resolve, 10));

      return new Promise((resolve, reject) => {
        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.state.apiURL + 'markings/collaborate',
            data: data,
            method: 'POST',
            headers: {"Authorization": "Bearer " + token}
          })
            .then(response => {
              this.state.isLoading = false;
              resolve(response.data);
            })
            .catch(err => {
              this.state.isLoading = false;
              console.log('Failed to sync collaborate markings', err);
              reject(err);
            });
        } else {
          this.state.isLoading = false;
        }
      });
    },
    async delete_file_markings({commit}, fileID) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Optimizing markings...';

      return new Promise((resolve, reject) => {
        idb.deleteFileMarkings(fileID).then(() => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }
          resolve();
        })
          .catch(err => {
            console.log('Failed to delete markings', err);
            this.state.isLoading = false;
            reject(err);
          });
      });
    },
    async replace_file_markings({commit}, {fileID, markings}) {
      return idb.replaceFileMarkings(fileID, markings);
    },
    async get_pdf_pages({commit}, fileID) {

      this.state.isLoading = true;
      this.state.loadingText = 'Getting PDF pages...';

      return new Promise((resolve, reject) => {
        idb.getPdfPages(fileID).then(pages => {
          resolve(pages);
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },
    async store_pdf_page({commit}, pdfPage) {
      // There is no state loading for this, it's handled elsewhere
      return new Promise((resolve, reject) => {
        idb.savePdfPage(pdfPage).then(() => {
          resolve(pdfPage);
        }).catch(err => {
          reject(err);
        });
      });
    },

    async delete_pdf_pages({commit}, fileID) {
      // There is no state loading for this, it's handled elsewhere
      return new Promise((resolve, reject) => {
        idb.deletePdfPages(fileID).then(() => {
          resolve();
        }).catch(err => {
          reject(err);
        });
      });
    },

    async get_projects() {

      this.state.isLoading = true;
      this.state.loadingText = 'Fetching projects...';

      return new Promise((resolve, reject) => {
        idb.getProjects().then(projects => {
          this.state.isLoading = false;
          resolve(projects);

        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },


    async sync() {

      this.state.isLoading = true;
      this.state.loadingText = 'Preparing to sync...';

      try {
        await this.dispatch('syncUser');
        await this.dispatch('syncCompanies');
        await this.dispatch('syncProjects');
      } catch (e) {
        console.log(e);
        throw 'Failed to sync';
      }

    },

    async syncUser({commit}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Synchronizing user...';

      return new Promise((resolve, reject) => {
        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.state.apiURL + 'auth/me',
            data: [],
            method: 'POST',
            headers: {"Authorization": "Bearer " + token}
          })
            .then(response => {

              let webUser = response.data;
              let newUser = null;

              idb.getUser().then(user => {

                if (moment(webUser.updated_at).isSame(user.updated_at)) {
                  this.state.isLoading = false;
                  resolve('User is the same');
                } else if (moment(webUser.updated_at).isAfter(user.updated_at)) {

                  idb.saveUser(webUser).then(r => {
                    this.state.isLoading = false;
                    commit('auth_success', {token, user});
                    resolve('User saved');
                  }).catch(err => {
                    this.state.isLoading = false;
                    reject(err);
                  });

                } else if (moment(webUser.updated_at).isBefore(user.updated_at)) {
                  axios({
                    url: this.state.apiURL + 'auth/me/update',
                    data: user,
                    method: 'POST',
                    headers: {"Authorization": "Bearer " + token}
                  }).then(response => {
                    webUser = response.data;
                    idb.saveUser(webUser).then(r => {
                      this.state.isLoading = false;
                      resolve('User saved');
                    }).catch(err => {
                      this.state.isLoading = false;
                      reject(err);
                    });
                  }).catch(err => {
                    this.state.isLoading = false;
                    reject(err);
                  });
                }
              });
            }).catch(err => {
            this.state.isLoading = false;
            reject(err);
          });
        }
      });
    },

    async syncCompanies() {

      this.state.isLoading = true;
      this.state.loadingText = 'Synchronizing companies...';

      return new Promise((resolve, reject) => {
        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.state.apiURL + 'auth/me/companies',
            data: [],
            method: 'GET',
            headers: {"Authorization": "Bearer " + token}
          })
            .then(response => {

              let companies = response.data;

              if (companies.length > 0) {
                idb.saveCompanies(companies).then(r => {
                  this.state.isLoading = false;
                  resolve('Companies synced');
                }).catch(err => {
                  this.state.isLoading = false;
                  reject(err);
                });
              } else {
                this.state.isLoading = false;
                resolve('Companies synced');
              }
            })
            .catch(err => {
              this.state.isLoading = false;
              reject(err);
            });
        }
      });
    },

    async syncProjects({commit}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Synchronizing projects...';

      return new Promise((resolve, reject) => {

        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.state.apiURL + 'auth/me/projects?is_public=1',
            data: [],
            method: 'GET',
            headers: {"Authorization": "Bearer " + token}
          })
            .then(response => {

              let projects = response.data.data;

              if (projects.length > 0) {
                idb.saveProjects(projects).then(r => {
                  this.state.isLoading = false;
                  resolve('Projects synced');
                });
              } else {
                this.state.isLoading = false;
                resolve('Projects synced');
              }
            })
            .catch(err => {
              this.state.isLoading = false;
              reject(err);
            });
        }
      });
    },

    async updateProject({commit}, project) {
      return new Promise((resolve, reject) => {
        idb.updateProject(project).then(r => {
          this.state.isLoading = false;
          resolve('Project updated');
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },

    async get_project({commit}, projectID) {
      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        this.state.loadingText = 'Getting project...';
        stopStateLoading = true;
      }


      return new Promise((resolve, reject) => {

        idb.getProject(projectID).then(project => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }
          resolve(project);
        }).catch(err => {

          this.state.isLoading = false;
          reject(err);
        });
      });

    },

    async get_project_folder({commit}, projectID) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        this.state.loadingText = 'Getting project folder...';
        stopStateLoading = true;
      }


      return new Promise((resolve, reject) => {
        this.dispatch('get_project', projectID).then(project => {

          idb.getFolders(projectID).then(folders => {

            if (folders.length === 0) {
              idb.saveFolder({
                project_id: project.id,
                name: project.name,
                parent_id: null,
                folder_id: null
              }).then(folder => {
                if (stopStateLoading) {
                  this.state.isLoading = false;
                }
                resolve(folder);
              });

            } else {
              resolve(folders[0]);
            }

          }).catch(err => {

            this.state.isLoading = false;
            reject(err);
          });

        });

      });

    },

    async get_folder({commit}, folder) {

      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.loadingText = 'Getting folder...';
        this.state.isLoading = true;
        stopStateLoading = true;
      }


      return new Promise((resolve, reject) => {
        this.dispatch('get_project_folder', folder.project_id).then(projectFolder => {

          this.state.loadingText = 'Getting folder...';

          idb.getFolder(folder.id).then(folderToReturn => {

            if (folderToReturn === null) {
              idb.saveFolder({
                project_id: folder.project_id,
                name: folder.name,
                parent_id: projectFolder.id,
                folder_id: folder.id
              }).then(folderToReturn => {
                if (stopStateLoading) {
                  this.state.isLoading = false;
                }
                resolve(folderToReturn);
              });

            } else {
              resolve(folderToReturn);
            }

          }).catch(err => {

            this.state.isLoading = false;
            reject(err);
          });

        });

      });

    },


    async syncProjectFiles({commit}, project) {

      this.state.isLoading = true;
      this.state.loadingText = 'Synchronizing project files...';

      return new Promise((resolve, reject) => {
        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.state.apiURL + 'companies/' + project.company_id + '/projects/' + project.id + '/files',
            params: {
              'is_public': 1
            },
            method: 'GET',
            headers: {"Authorization": "Bearer " + token}
          })
            .then(response => {
              const webFiles = response.data.data;
              this.state.isLoading = false;
              resolve(webFiles);

            })
            .catch(err => {
              this.state.isLoading = false;
              reject(err);
            });
        }
      });
    },

    async sendReport({commit}, data) {

      this.state.isLoading = true;
      this.state.loadingText = 'Sending report...';

      return new Promise((resolve, reject) => {
        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.state.apiURL + 'reports',
            data: data,
            method: 'POST',
            headers: {"Authorization": "Bearer " + token}
          })
            .then(response => {
              if (response.status < 299) {
                this.state.isLoading = false;
                resolve(response.data.data);
              } else {
                this.state.isLoading = false;
                alert('Failed to send report');
                reject(response);
              }
            })
            .catch(err => {
              this.state.isLoading = false;
              handleApiError(err, 'Failed to send report.');
              reject(err);
            });
        }
      });
    },
    async updateReport({commit}, {reportID, formData}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Sending report...';


      return new Promise((resolve, reject) => {

        let token = localStorage.getItem('token');

        if (token) {

          formData.set('_method', 'PUT');

          axios({
            url: this.getters.apiProjectBase + '/reports/' + reportID,
            data: formData,
            method: 'POST',
            headers: {"Authorization": "Bearer " + token}
          })
            .then(response => {
              this.state.isLoading = false;
              resolve(response.data.data);
            })
            .catch(err => {
              this.state.isLoading = false;
              handleApiError(err, 'Failed to update report.');
              reject(err);
            });
        }
      });
    },

    async uploadChangeLayout({commit}, {file}) {
      this.state.isLoading = true;
      this.state.loadingText = 'Uploading PDF...';

      let token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      return axios({
        url: '/api/reports/change-layout',
        method: 'POST',
        headers: {
          "Authorization": "Bearer " + token,
          "Content-Type": "multipart/form-data"
        },
        data: formData
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data;
        })
        .catch(error => {
          this.state.isLoading = false;
          console.error('Error uploading PDF:', error);
          throw error;
        });
    },


    async loadFiles({commit}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading files...';

      let token = localStorage.getItem('token');

      return axios({
        url: this.getters.apiProjectBase + '/files',
        method: 'GET',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return [];
        });
    },

    async loadMarkingsForFile({commit}, fileID) {
      this.state.isLoading = true;
      this.state.loadingText = 'Loading markings...';
      let token = localStorage.getItem('token');
      let data = {};
      return axios({
        url: `${this.getters.apiProjectBase}/files/${fileID}/markings`,
        method: 'GET',
        data: data,
        headers: {"Authorization": `Bearer ${token}`}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data.markings;
        })
        .catch(error => {
          console.error("Failed to load markings:", error);
          this.state.isLoading = false;
          return [];
        });
    },

    async loadMarking({commit}, uuid) {
      this.state.isLoading = true;
      this.state.loadingText = 'Loading marking...';

      let token = localStorage.getItem('token');

      try {
        const response = await axios({
          url: this.state.apiURL + `marking/${uuid}`,
          method: 'GET',
          headers: {"Authorization": "Bearer " + token}
        });

        this.state.isLoading = false;
        return response.data.data;

      } catch (error) {
        this.state.isLoading = false;
        console.error('Error loading marking:', error);
        return {error};
      }
    },

    async loadMarkings(fileID) {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading markings...';

      let token = localStorage.getItem('token');

      let data = {};

      if (fileID) {
        data.file_id = fileID;
      }

      return axios({
        url: this.getters.apiProjectBase + '/markings',
        method: 'GET',
        data: data,
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return [];
        });
    },

    async loadPages(fileID) {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading pages...';

      let token = localStorage.getItem('token');

      let data = {};

      if (fileID) {
        data.file_id = fileID;
      }

      return axios({
        url: this.getters.apiProjectBase + '/pages',
        method: 'GET',
        data: data,
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return [];
        });
    },

    async loadReports({commit}, currentReportID = null) {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading reports...';

      let token = localStorage.getItem('token');

      return axios({
        url: this.getters.apiProjectBase + '/reports',
        params: {
          'current_report_id': currentReportID
        },
        method: 'GET',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return [];
        });
    },

    async loadReport({commit}, reportID) {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading report...';

      let token = localStorage.getItem('token');

      return axios({
        url: this.getters.apiProjectBase + '/reports/' + reportID,
        method: 'GET',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return [];
        });
    },

    async loadSubscriptions({commit}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading subscriptions...';

      let token = localStorage.getItem('token');

      return axios({
        url: this.getters.apiCompanyBase + '/subscriptions',
        method: 'GET',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return [];
        });
    },

    async loadSubscriptionIntent({commit}, data) {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading subscriptions...';

      let token = localStorage.getItem('token');

      return axios({
        url: this.getters.apiCompanyBase + '/subscriptions/intent',
        data: data,
        method: 'PUT',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return Promise.reject(response);
        });
    },

    async createSubscription({commit}, data) {

      this.state.isLoading = true;
      this.state.loadingText = 'Processing subscription...';

      let token = localStorage.getItem('token');

      return axios({
        url: this.getters.apiCompanyBase + '/subscriptions',
        data: data,
        method: 'POST',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return Promise.reject(response);
        });
    },

    async cancelSubscription({commit}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Cancelling subscription...';

      let token = localStorage.getItem('token');

      return axios({
        url: this.getters.apiCompanyBase + '/subscriptions/cancel',
        method: 'DELETE',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return Promise.reject(response);
        });
    },

    async resumeSubscription({commit}, data) {

      this.state.isLoading = true;
      this.state.loadingText = 'Resuming subscription...';

      let token = localStorage.getItem('token');

      return axios({
        url: this.getters.apiCompanyBase + '/subscriptions/resume',
        method: 'PUT',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return Promise.reject(response);
        });
    },

    async swapSubscription({commit}, data) {

      this.state.isLoading = true;
      this.state.loadingText = 'Changing subscription...';

      let token = localStorage.getItem('token');

      return axios({
        url: this.getters.apiCompanyBase + '/subscriptions/swap',
        data: data,
        method: 'PUT',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return Promise.reject(response);
        });
    },

    async loadCompanies() {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading companies...';

      let token = localStorage.getItem('token');

      return axios({
        url: this.state.apiURL + 'auth/me/companies',
        method: 'GET',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {

          this.state.isLoading = false;
          this.state.companies = response.data;

          idb.saveCompanies(response.data);

          return response.data;

        })
        .catch(response => {
          this.state.companies = [];
          this.state.isLoading = false;
          return [];
        });
    },
    async loadCompany() {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading company...';

      let token = localStorage.getItem('token');

      return axios({
        url: this.getters.apiBase,
        method: 'GET',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return [];
        });
    },

    async loadProject({commit}, projectId) {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading project...';

      let token = localStorage.getItem('token');

      return axios({
        url: this.getters.apiBase + '/projects/' + projectId,
        method: 'GET',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          this.dispatch('setProject', response.data.data);

          return response.data.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          this.dispatch('setProject', null);
          return null;  // was: return []
        });
    },


    setProject: function ({commit}, project) {
      commit('project', project);
    },

    async loadUsers({commit}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading users...';

      let token = localStorage.getItem('token');
      return axios({
        url: this.getters.apiCompanyBase + '/users',
        method: 'GET',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return [];
        });
    },
    async loadProjectUsers({commit}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading users...';

      let token = localStorage.getItem('token');
      return axios({
        url: this.getters.apiProjectBase + '/users',
        method: 'GET',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return [];
        });
    },
    async loadRoles({commit}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading roles...';

      let token = localStorage.getItem('token');
      return axios({
        url: this.getters.apiBase + '/roles',
        method: 'GET',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return [];
        });
    },
    async loadLoggedInUser({commit}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Loading user...';

      let token = localStorage.getItem('token');
      return axios({
        url: this.state.apiURL + 'auth/me',
        method: 'POST',
        headers: {"Authorization": "Bearer " + token}
      })
        .then(response => {
          this.state.isLoading = false;
          return response.data;
        })
        .catch(response => {
          this.state.isLoading = false;
          return [];
        });
    },
    async getFileMarkings({commit}, fileID) {
      this.state.isLoading = true;
      this.state.loadingText = 'Processing markings...';
      await new Promise(resolve => setTimeout(resolve, 10));

      let file = null;
      let filePages = [];
      let fileMarkings = [];

      return this.dispatch('get_file', fileID)
        .then(item => {
          file = item;
          let pages = this.dispatch('get_pdf_pages', fileID);

          this.state.isLoading = true;
          this.state.loadingText = 'Processing markings...';

          return pages;

        })
        .then(pages => {
          filePages = pages.sort((a, b) => a.page - b.page);

          return this.dispatch('get_markings', fileID);
        })
        .then(markings => {
          for (let i = 0; i < markings.length; i++) {
            if (markings[i].type === 'draw' && markings[i].attachments && markings[i].attachments.length > 0) {
              markings[i].drawing = markings[i].attachments[0];
              markings[i].attachments.splice(0, 1);
            }

            if (markings[i].attachments && markings[i].attachments.length > 0) {
              markings[i].groupped_attachments = Array(Math.ceil(markings[i].attachments.length / 2)).fill().map(_ => markings[i].attachments.splice(0, 2));
              markings[i].attachments = markings[i].groupped_attachments.flat();
            }
          }

          fileMarkings = markings.sort((a, b) => a.page - b.page);
        })
        .then(() => {
          if (!file || !filePages || filePages.length === 0 || !fileMarkings || fileMarkings.length === 0) {
            return Promise.reject(new Error('No markings found'));
          }

          return this.dispatch('prepareFileMarkings', {
            file: file,
            pages: filePages,
            markings: fileMarkings
          });
        })
        .catch((err) => {
          this.state.isLoading = false;

          return Promise.reject(err);
        });
    },
    async prepareFileMarkings({commit}, {file, pages, markings}) {
      if (!file || !pages || pages.length === 0 || !markings || markings.length === 0) {
        return Promise.reject(new Error('No markings to prepare'));
      }
      this.state.isLoading = true;
      this.state.loadingText = 'Processing markings...';

      return new Promise((resolve, reject) => {
        this.state.isLoading = false;
        resolve({file, pages, markings});
      });
    },

    async APIChangeLogToMarkingAttachment({ commit },  {changeLogMediaID, markingID}) {
      this.state.isLoading = true;
      this.state.loadingText = 'Processing Attachment...';

      const token = localStorage.getItem('token');

      if (!token) {
        this.state.isLoading = false;
        throw new Error('No auth token found');
      }

      try {
        const response = await axios.post(
          this.state.apiURL+'changelog-media/'+changeLogMediaID+'/marking/'+markingID+'/attach',
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        this.state.isLoading = false;
        return response.data;
      } catch (err) {
        this.state.isLoading = false;
        throw err;
      }
    },

/*    TODO: Remove if sending changelogs with sync works.

      async addChangeLog({commit}, formData) {
      this.state.isLoading = true;
      this.state.loadingText = 'Uploading Changelog...';

      const token = localStorage.getItem('token');
      if (!token) {
        this.state.isLoading = false;
        throw new Error('No auth token found');
      }

      try {
        const response = await axios.post(
          this.state.apiURL + 'change-logs',formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            }
          }
        );

        //this.state.isLoading = false;
        console.log('Attachment added', response.data);
        return response.data;
      } catch (err) {
        //this.state.isLoading = false;
        console.error('Failed to add attachment', err);
        throw err;
      }
    },*/

    async syncFileMarkings({commit}, data) {

      this.state.isLoading = true;
      this.state.loadingText = 'Synchronizing file markings...';
      await new Promise(resolve => setTimeout(resolve, 10));

      return new Promise((resolve, reject) => {
        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.state.apiURL + 'markings',
            data: data,
            method: 'POST',
            headers: {"Authorization": "Bearer " + token}
          })
            .then(response => {

              this.state.isLoading = false;
              //console.log('Markings synced', response.data);
              resolve(response.data.data);
            })
            .catch((err, data) => {
              this.state.isLoading = false;
              // Sync has its own error handler
              console.log('Failed to sync markings', err);
              //handleApiError(err, 'Failed to sync markings');
              reject(err);
            });
        } else {
          this.state.isLoading = false;
        }
      });
    },

    async APIgetFilePages({commit}, data) {

      this.state.isLoading = true;
      this.state.loadingText = 'Synchronizing layout...';
      await new Promise(resolve => setTimeout(resolve, 10));

      return new Promise((resolve, reject) => {
        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.state.apiURL + 'pages',
            params: data,
            method: 'GET',
            headers: {"Authorization": "Bearer " + token}
          })
            .then(response => {

              this.state.isLoading = false;
              resolve(response.data);
            })
            .catch((err, data) => {
              this.state.isLoading = false;
              handleApiError(err, 'Failed to sync layout');
              reject(err);
            });
        } else {
          this.state.isLoading = false;
        }
      });
    },

    async saveToReportArchive({commit}, report) {
      let stopStateLoading = true;

      if (this.state.isLoading) {
        stopStateLoading = false;
      }

      this.state.isLoading = true;
      this.state.loadingText = 'Synchronizing projects...';

      return new Promise((resolve, reject) => {
        idb.saveReport(report).then(r => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }
          resolve(report);
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });

    },
    async get_archived_reports({commit}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Fetching reports...';

      return new Promise((resolve, reject) => {
        idb.getReports().then(reports => {
          this.state.isLoading = false;
          resolve(reports);

        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });
    },
    async delete_report({commit}, reportID) {
      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Deleting report...';

      return new Promise((resolve, reject) => {


        idb.deleteReport(reportID).then(() => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }

          resolve();
        }).catch(err => {
          this.state.isLoading = false;
          handleApiError(err, 'Failed to delete report');
          reject(err);
        });
      });

    },
    async get_archived_report({commit}, reportID) {
      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Getting report...';

      return new Promise((resolve, reject) => {

        idb.getArchivedReport(reportID).then(report => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }
          resolve(report);
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });

    },
    async APIdeleteReport({commit}, reportID) {

      this.state.isLoading = true;
      this.state.loadingText = 'Deleting report...';

      return new Promise((resolve, reject) => {
        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.getters.apiProjectBase + '/reports/' + reportID,
            data: {},
            method: 'DELETE',
            headers: {"Authorization": "Bearer " + token}
          })
            .then(response => {
              this.state.isLoading = false;
              resolve(response.data);
            })
            .catch(err => {
              this.state.isLoading = false;
              console.log('error', err);
              reject(err);
            });
        }
      });
    },
    async APIgetFileWithMarkings({commit}, {file_id, user_id}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Getting file...';

      return new Promise((resolve, reject) => {

        let data = {};

        if (user_id) {
          data.user_id = user_id;
        }

        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.getters.apiProjectBase + '/files/' + file_id + '/markings',
            data: data,
            method: 'GET',
            headers: {"Authorization": "Bearer " + token}
          })
            .then(response => {
              this.state.isLoading = false;
              resolve(response.data);
            })
            .catch(err => {
              this.state.isLoading = false;
              handleApiError(err, 'Failed to get file.');
              reject(err);
            });
        }
      });
    },

    async APIgetMarkingChangelogAttachments({commit}, {file_id, marking_id}) {
      this.state.isLoading = true;
      this.state.loadingText = 'Getting Changelog Attachments...';

      return new Promise((resolve, reject) => {

        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.state.apiURL + 'files/' + file_id + '/markings/' + marking_id + '/changelog-attachments',
            method: 'GET',
            headers: {"Authorization": "Bearer " + token},
          })
            .then(response => {
              this.state.isLoading = false;
              resolve(response.data.data);
            })
            .catch(err => {
              this.state.isLoading = false;
              handleApiError(err, 'Failed to get changelog Attachments.');
              reject(err);
            });
        }
      });
    },

    async APIgetMarkingComments({commit}, {file_id, marking_id}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Getting comments...';

      return new Promise((resolve, reject) => {

        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.state.apiURL + 'files/' + file_id + '/markings/' + marking_id + '/comments',
            method: 'GET',
            headers: {"Authorization": "Bearer " + token},
          })
            .then(response => {
              this.state.isLoading = false;
              resolve(response.data.data);
            })
            .catch(err => {
              this.state.isLoading = false;
              handleApiError(err, 'Failed to get comments.');
              reject(err);
            });
        }
      });
    },

    async APIpostMarkingComment({commit}, {file_id, marking_id, comment}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Posting comment...';

      return new Promise((resolve, reject) => {

        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.state.apiURL + 'files/' + file_id + '/markings/' + marking_id + '/comments',
            data: {
              comment: comment,
            },
            method: 'POST',
            headers: {"Authorization": "Bearer " + token},
          })
            .then(response => {
              this.state.isLoading = false;
              resolve(response.data.data);
            })
            .catch(err => {
              this.state.isLoading = false;
              handleApiError(err, 'Failed to submit your comment. Please try again later.');
              reject(err);
            });
        }
      });
    },


    async APIgetProjectTools({commit}, projectID) {
      if (isNaN(projectID)) {
        console.log('Invalid projectId for APIgetProjectTools', projectID)
        return;
      }

      this.state.isLoading = true;
      this.state.loadingText = 'Getting project tools...';

      return new Promise((resolve, reject) => {

        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.getters.apiProjectBase + '/marking-tools',
            method: 'GET',
            headers: {"Authorization": "Bearer " + token}
          })
            .then(response => {
              this.state.isLoading = false;

              const tools = response.data.data;
              resolve(tools);

            })
            .catch(err => {
              this.state.isLoading = false;
              reject(err);
            });
        }
      });
    },


    async get_project_tools({commit}, projectID) {
      let stopStateLoading = false;

      if (!this.state.isLoading) {
        this.state.isLoading = true;
        stopStateLoading = true;
      }

      this.state.loadingText = 'Getting report...';

      return new Promise((resolve, reject) => {

        idb.getProjectTools(projectID).then(tools => {
          if (stopStateLoading) {
            this.state.isLoading = false;
          }
          resolve(tools);
        }).catch(err => {
          this.state.isLoading = false;
          reject(err);
        });
      });

    },

    async save_project_tools({commit}, {projectID, tools}) {

      this.state.isLoading = true;
      this.state.loadingText = 'Saving tools...';

      return new Promise((resolve, reject) => {
        idb.deleteProjectTools(projectID).then(() => {
          idb.saveProjectTools(tools).then(() => {
            this.state.isLoading = false;
            resolve(tools);
          });
        });
      });
    },

    async APIgetFileMarkings({commit}, fileId) {
      this.state.isLoading = true;
      this.state.loadingText = 'Fetching markings...';

      return new Promise((resolve, reject) => {

        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.getters.apiProjectBase + '/files/' + fileId + '/markings',
            method: 'GET',
            headers: {"Authorization": "Bearer " + token},
            params: {
              filter_by: 'user',
              without_trashed: false,
              without_trashed_media: true
            }
          })
            .then(response => {
              this.state.isLoading = false;
              resolve(response.data.markings);
            })
            .catch(err => {
              console.log('error', err);
              this.state.isLoading = false;
              reject(err);
            });
        }
      });
    },

    // Collaborate mode: fetch a teammate's markings or, when userId is omitted, every user's markings for the file. Separate from APIgetFileMarkings.
    async APIgetCollaborateMarkings({commit}, {fileId, userId = null}) {
      this.state.isLoading = true;
      this.state.loadingText = 'Fetching collaborate markings...';

      return new Promise((resolve, reject) => {

        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.getters.apiProjectBase + '/files/' + fileId + '/markings',
            method: 'GET',
            headers: {"Authorization": "Bearer " + token},
            params: {
              user_id: userId,
              without_trashed: true,
              without_trashed_media: true
            }
          })
            .then(response => {
              this.state.isLoading = false;
              resolve(response.data.markings);
            })
            .catch(err => {
              console.log('error', err);
              this.state.isLoading = false;
              reject(err);
            });
        }
      });
    },

    async APIcancelReport({commit}, reportID) {

      this.state.isLoading = true;
      this.state.loadingText = 'Canceling report...';

      return new Promise((resolve, reject) => {
        let token = localStorage.getItem('token');
        if (token) {
          axios({
            url: this.getters.apiProjectBase + '/reports/' + reportID + '/cancel',
            data: {},
            method: 'GET',
            headers: {"Authorization": "Bearer " + token}
          })
            .then(response => {
              this.state.isLoading = false;
              resolve(response.data.data);
            })
            .catch(err => {
              this.state.isLoading = false;
              console.log('error', err);
              reject(err);
            });
        }
      });
    },

    async getFileMarkingIndex({commit}, data) {
      this.state.isLoading = true;
      this.state.loadingText = 'Calculating marking index...';

      return new Promise(async (resolve, reject) => {
        if (
          typeof data.fileID === 'undefined' ||
          typeof data.markingPrefix === 'undefined' ||
          typeof data.markingType === 'undefined'
        ) {
          this.state.isLoading = false;
          reject('Invalid data');
        }
        this.state.isLoading = true;
        const file = await this.dispatch('get_file', data.fileID);

        this.state.isLoading = false;

        if (!file) {
          reject('File not found');
          return;
        }

        if (typeof file.markingTypeIndexes === 'undefined') {
          file.markingTypeIndexes = {};
        }

        let currentMarkingTypeIndex = file.markingTypeIndexes[data.markingType + data.markingPrefix];

        resolve(currentMarkingTypeIndex ? (parseInt(file.markingTypeIndexes[data.markingType + data.markingPrefix]) + 1) : 1);
      });
    },

    async updateFileMarkingTypeIndex({commit}, data) {
      this.state.isLoading = true;
      this.state.loadingText = 'Updating file marking index...';

      return new Promise(async (resolve, reject) => {
        if (
          typeof data.fileID === 'undefined' ||
          typeof data.markingPrefix === 'undefined' ||
          typeof data.markingIndex === 'undefined' ||
          typeof data.markingType === 'undefined'
        ) {
          this.state.isLoading = false;
          reject('Invalid data');
        }
        const file = await this.dispatch('get_file', data.fileID);
        if (!file) {
          this.state.isLoading = false;
          reject('File not found');
          return;
        }

        if (typeof file.markingTypeIndexes === 'undefined') {
          file.markingTypeIndexes = {};
        }

        const currentMarkingTypeIndex = file.markingTypeIndexes[data.markingType + data.markingPrefix];

        if (
          typeof currentMarkingTypeIndex === 'undefined' ||
          currentMarkingTypeIndex < data.markingIndex) {

          file.markingTypeIndexes[data.markingType + data.markingPrefix] = data.markingIndex;

          idb.saveFile(file).then(() => {
            this.state.isLoading = false;
            resolve();
          }).catch(err => {
            this.state.isLoading = false;
            reject(err);
          });
        } else {
          this.state.isLoading = false;
          resolve();
        }
      });
    },


  },


  getters: {
    hasUnauthTrial: state => !state.token && !!state.unauthTrialEndsAt,
    unauthTrialEndsAt: state => state.unauthTrialEndsAt,
    hasUnauthTrialEnded: state => state.unauthTrialEndsAt &&
      moment().isAfter(moment(state.unauthTrialEndsAt)),
    isLoggedIn: state => !!state.token,
    authStatus: state => state.status,
    project: state => 1,
    mangerURL: state => API_DOMAIN,
    apiBase: state => API_DOMAIN + '/api/companies/' + state.company.id,
    apiCompanyBase: state => API_DOMAIN + '/api/companies/' + state.company.id,
    apiProjectBase: state => API_DOMAIN + '/api/companies/' + state.company.id + '/projects/' + state.project.id,
  }
});

function handleApiError(error, defaultErrorMessage) {
  let errorMessage = defaultErrorMessage ? defaultErrorMessage : 'An eror has occured. Please try again later.';
  if (error.response && typeof error.response.data.error !== 'undefined') {
    errorMessage = error.response.data.error;
  }

  // Note: Bugsnag reporting is handled in App.vue HTTP interceptor to avoid duplicate reports
  // This function only handles user-facing error messages

  alert(errorMessage);
}
